"use strict";
/**
 * Skin Report Constants
 *
 * Constants and enums for skin report generation.
 * Follows pattern from skinAi.ts
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.routineAdherenceLevelEnum = exports.RoutineAdherenceLevel = exports.reportSentimentEnum = exports.ReportSentiment = exports.summaryMagnitudeEnum = exports.SummaryMagnitude = exports.openItemPriorityEnum = exports.OpenItemPriority = exports.openItemStatusEnum = exports.OpenItemStatus = exports.openItemTypeEnum = exports.OpenItemType = exports.predictionDirectionEnum = exports.PredictionDirection = exports.predictionOutcomeEnum = exports.PredictionOutcome = exports.predictionStatusEnum = exports.PredictionStatus = exports.AdherenceConsistency = exports.EvaluationTimelines = exports.skinReportStatusEnum = exports.SkinReportStatus = exports.skinReportTierEnum = exports.SkinReportTier = exports.skinReportTypeEnum = exports.SkinReportType = exports.severityLevelEnum = exports.SeverityLevel = exports.trajectoryDirectionEnum = exports.TrajectoryDirection = void 0;
exports.getAdherenceConsistency = getAdherenceConsistency;
/**
 * Trajectory Direction
 *
 * How a metric is trending over the report period
 */
exports.TrajectoryDirection = {
    IMPROVING: 'improving',
    WORSENING: 'worsening',
    STABLE: 'stable',
    FLUCTUATING: 'fluctuating',
};
exports.trajectoryDirectionEnum = Object.values(exports.TrajectoryDirection);
/**
 * Severity Levels
 *
 * Clinical severity classification for skin conditions
 */
exports.SeverityLevel = {
    NONE: 'none',
    MINIMAL: 'minimal',
    MILD: 'mild',
    MODERATE: 'moderate',
    SEVERE: 'severe',
};
exports.severityLevelEnum = Object.values(exports.SeverityLevel);
/**
 * Report Types
 *
 * first: User's first skin report (no prior report exists)
 * subsequent: Any report after the first (has prior report for comparison)
 */
exports.SkinReportType = {
    FIRST: 'first',
    SUBSEQUENT: 'subsequent',
};
exports.skinReportTypeEnum = Object.values(exports.SkinReportType);
/**
 * Report Tiers
 *
 * face_only: Free tier - face-level analysis only
 * face_regional: Premium tier - face + regional analysis
 */
exports.SkinReportTier = {
    FACE_ONLY: 'face_only',
    FACE_REGIONAL: 'face_regional',
};
exports.skinReportTierEnum = Object.values(exports.SkinReportTier);
/**
 * Report Status
 *
 * Pipeline status tracking:
 * - pending: Report created, not started
 * - analyzing: Stage 1 running (per-dimension analysis)
 * - analysis_complete: Stage 1 done, waiting for Stage 3
 * - routine_context_complete: Stage 3 done (routine context analysis for first report)
 * - comparison_complete: Stage 2 done (longitudinal comparison - subsequent reports only)
 * - recommendations_complete: Stage 4 done (recommendations)
 * - completed: Stage 5 done (narrative), report ready
 * - failed: Pipeline failed at any stage
 */
exports.SkinReportStatus = {
    PENDING: 'pending',
    ANALYZING: 'analyzing',
    ANALYSIS_COMPLETE: 'analysis_complete',
    ROUTINE_CONTEXT_COMPLETE: 'routine_context_complete',
    COMPARISON_COMPLETE: 'comparison_complete',
    RECOMMENDATIONS_COMPLETE: 'recommendations_complete',
    COMPLETED: 'completed',
    FAILED: 'failed',
};
exports.skinReportStatusEnum = Object.values(exports.SkinReportStatus);
/**
 * Evaluation Timelines for Recommendation Lifecycle
 *
 * Timelines in weeks for evaluating product/treatment effectiveness
 */
exports.EvaluationTimelines = {
    /** Trial period - too early to evaluate effectiveness */
    TRIAL_PERIOD_WEEKS: 4,
    /** Evaluation window - ready for preliminary assessment */
    EVALUATION_WINDOW_WEEKS: 8,
    /** Established period - long-term confirmed effectiveness */
    ESTABLISHED_WEEKS: 12,
};
/**
 * Adherence Consistency Levels
 *
 * Categorical consistency levels for routine adherence rates.
 * Used to determine if a product can be evaluated for efficacy.
 *
 * - HIGHLY_CONSISTENT (≥80%): Can fully evaluate product efficacy
 * - MOSTLY_CONSISTENT (60-79%): Can partially evaluate
 * - SOMEWHAT_CONSISTENT (40-59%): Cannot reliably evaluate efficacy
 * - INCONSISTENT (20-39%): Cannot evaluate efficacy
 * - VERY_INCONSISTENT (<20%): Cannot evaluate efficacy
 * - NOT_APPLICABLE: For as-needed products (spot treatments, masks)
 */
exports.AdherenceConsistency = {
    HIGHLY_CONSISTENT: 'highly_consistent',
    MOSTLY_CONSISTENT: 'mostly_consistent',
    SOMEWHAT_CONSISTENT: 'somewhat_consistent',
    INCONSISTENT: 'inconsistent',
    VERY_INCONSISTENT: 'very_inconsistent',
    NOT_APPLICABLE: 'not_applicable',
};
/**
 * Convert adherence rate (0-1) to consistency level.
 * Returns NOT_APPLICABLE for null/undefined rates (as-needed products).
 */
function getAdherenceConsistency(rate) {
    if (rate === null || rate === undefined) {
        return exports.AdherenceConsistency.NOT_APPLICABLE;
    }
    if (rate >= 0.8)
        return exports.AdherenceConsistency.HIGHLY_CONSISTENT; // 80-100%
    if (rate >= 0.6)
        return exports.AdherenceConsistency.MOSTLY_CONSISTENT; // 60-79%
    if (rate >= 0.4)
        return exports.AdherenceConsistency.SOMEWHAT_CONSISTENT; // 40-59%
    if (rate >= 0.2)
        return exports.AdherenceConsistency.INCONSISTENT; // 20-39%
    return exports.AdherenceConsistency.VERY_INCONSISTENT; // 0-20%
}
// ============================================================================
// Longitudinal Tracking Enums
// ============================================================================
/**
 * Prediction Status
 *
 * Tracks the lifecycle of a prediction made in a skin report.
 * - pending: Prediction made, not yet evaluated (waiting for next report)
 * - confirmed: Prediction was accurate
 * - partially_confirmed: Prediction was partially accurate
 * - refuted: Prediction was incorrect
 * - superseded: Prediction is no longer relevant (e.g., user changed routine)
 */
exports.PredictionStatus = {
    PENDING: 'pending',
    CONFIRMED: 'confirmed',
    PARTIALLY_CONFIRMED: 'partially_confirmed',
    REFUTED: 'refuted',
    SUPERSEDED: 'superseded',
};
exports.predictionStatusEnum = Object.values(exports.PredictionStatus);
/**
 * Prediction Outcome
 *
 * What actually happened with the metric after the prediction was made.
 * Used when resolving a prediction.
 */
exports.PredictionOutcome = {
    IMPROVED: 'improved',
    WORSENED: 'worsened',
    STABLE: 'stable',
    UNKNOWN: 'unknown',
};
exports.predictionOutcomeEnum = Object.values(exports.PredictionOutcome);
/**
 * Prediction Direction
 *
 * The predicted direction of change for a metric.
 */
exports.PredictionDirection = {
    IMPROVE: 'improve',
    WORSEN: 'worsen',
    STABLE: 'stable',
};
exports.predictionDirectionEnum = Object.values(exports.PredictionDirection);
/**
 * Open Item Type
 *
 * Categories of items that need follow-up between reports.
 * - concern: A skin concern that needs monitoring
 * - recommendation_followup: Check if a recommendation was applied
 * - investigation: Something that needs further analysis
 * - routine_change: A routine change that needs evaluation
 */
exports.OpenItemType = {
    CONCERN: 'concern',
    RECOMMENDATION_FOLLOWUP: 'recommendation_followup',
    INVESTIGATION: 'investigation',
    ROUTINE_CHANGE: 'routine_change',
};
exports.openItemTypeEnum = Object.values(exports.OpenItemType);
/**
 * Open Item Status
 *
 * Lifecycle status of an open item.
 * - open: Item is active and needs attention
 * - resolved: Item has been addressed
 * - superseded: Item is no longer relevant
 * - dismissed: Item was dismissed by user or system
 */
exports.OpenItemStatus = {
    OPEN: 'open',
    RESOLVED: 'resolved',
    SUPERSEDED: 'superseded',
    DISMISSED: 'dismissed',
};
exports.openItemStatusEnum = Object.values(exports.OpenItemStatus);
/**
 * Open Item Priority
 *
 * Priority level for follow-up items.
 */
exports.OpenItemPriority = {
    HIGH: 'high',
    MEDIUM: 'medium',
    LOW: 'low',
};
exports.openItemPriorityEnum = Object.values(exports.OpenItemPriority);
// ============================================================================
// Report Summary Enums
// ============================================================================
/**
 * Summary Magnitude
 *
 * Magnitude of a metric change in a report summary.
 */
exports.SummaryMagnitude = {
    SIGNIFICANT: 'significant',
    MODERATE: 'moderate',
    MINOR: 'minor',
};
exports.summaryMagnitudeEnum = Object.values(exports.SummaryMagnitude);
/**
 * Report Sentiment
 *
 * Overall sentiment of a skin report.
 */
exports.ReportSentiment = {
    POSITIVE: 'positive',
    NEUTRAL: 'neutral',
    CONCERNING: 'concerning',
};
exports.reportSentimentEnum = Object.values(exports.ReportSentiment);
/**
 * Routine Adherence Level
 *
 * Simplified adherence classification for report summaries.
 * Used in RoutineSummary JSONB field.
 */
exports.RoutineAdherenceLevel = {
    HIGH: 'high',
    MODERATE: 'moderate',
    LOW: 'low',
    INSUFFICIENT: 'insufficient',
};
exports.routineAdherenceLevelEnum = Object.values(exports.RoutineAdherenceLevel);
//# sourceMappingURL=skinReport.js.map